import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputBase,
    Menu,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    ArrowForwardRounded,
    ChevronRightRounded,
    CloseRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import ObjectIcon from "../components/ObjectIcon";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import { Button } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import { can } from "../utils/session";
import CreateRecordAction from "../components/CreateRecordAction";
import { createPipeline, listOrgRoles, listPipelines, savePipeline } from "../api";
import {
    ACTION_TYPES,
    OPS,
    STAGE_COLORS,
    apiError,
    opNeedsValue,
} from "../utils/meta";

const NODE_W = 190;
const NODE_H = 70;
const ANY_NODE = { x: 40, y: 32, w: 72, h: 30 };

const SECTION_SX = {
    fontSize: 12,
    letterSpacing: "1.5px",
    fontWeight: 700,
    color: "var(--text-strong-muted)",
};

const PANEL_LABEL_SX = {
    fontSize: 11,
    letterSpacing: "2px",
    fontWeight: 700,
    color: "var(--text-tertiary)",
};

let tmpCounter = 0;
const tmpKey = (prefix) => `${prefix}-tmp-${++tmpCounter}`;

/* ------------------------------------------------------------------ */
/* Graph mapping: API <-> local (client keys survive unsaved stages)    */
/* ------------------------------------------------------------------ */

function toLocalGraph(p) {
    const stages = (p.stages ?? [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((s, i) => ({
            key: String(s.id),
            id: s.id,
            name: s.name,
            color: s.color || STAGE_COLORS[i % STAGE_COLORS.length],
            kind: s.kind || "open",
            probability: s.probability ?? "",
            x: s.x ?? 56 + (i % 3) * 274,
            y: s.y ?? 146 + Math.floor(i / 3) * 212,
        }));
    const transitions = (p.transitions ?? []).map((t) => ({
        key: t.id ? String(t.id) : tmpKey("t"),
        id: t.id ?? null,
        fromKey: t.fromStageId == null ? null : String(t.fromStageId),
        toKey: String(t.toStageId),
        guards: t.guards ?? [],
        roleIds: t.roleIds ?? null,
        actions: t.actions ?? [],
        isActive: t.isActive !== false,
    }));
    return { stages, transitions };
}

function toPayload(name, stages, transitions) {
    const ref = (key) => {
        const idx = stages.findIndex((s) => s.key === key);
        const stage = stages[idx];
        return stage?.id ?? `idx:${idx}`;
    };
    return {
        name,
        stages: stages.map((s, i) => ({
            ...(s.id ? { id: s.id } : {}),
            name: s.name,
            color: s.color,
            kind: s.kind,
            probability:
                s.probability === "" || s.probability === null
                    ? null
                    : Number(s.probability),
            position: i,
            x: Math.round(s.x),
            y: Math.round(s.y),
        })),
        transitions: transitions.map((t) => ({
            fromStageId: t.fromKey == null ? null : ref(t.fromKey),
            toStageId: ref(t.toKey),
            guards: t.guards,
            roleIds: t.roleIds,
            actions: t.actions,
            isActive: t.isActive,
        })),
    };
}

/* ------------------------------------------------------------------ */
/* Bezier geometry                                                      */
/* ------------------------------------------------------------------ */

function edgeGeometry(from, to) {
    const acx = from.x + from.w / 2;
    const acy = from.y + from.h / 2;
    const bcx = to.x + to.w / 2;
    const bcy = to.y + to.h / 2;
    const dx = bcx - acx;
    const dy = bcy - acy;

    let sx, sy, ex, ey, c1x, c1y, c2x, c2y;
    if (Math.abs(dx) >= Math.abs(dy)) {
        sx = dx >= 0 ? from.x + from.w : from.x;
        ex = dx >= 0 ? to.x : to.x + to.w;
        sy = acy;
        ey = bcy;
        const off = Math.max(36, Math.abs(ex - sx) * 0.4) * (dx >= 0 ? 1 : -1);
        c1x = sx + off;
        c1y = sy;
        c2x = ex - off;
        c2y = ey;
    } else {
        sy = dy >= 0 ? from.y + from.h : from.y;
        ey = dy >= 0 ? to.y : to.y + to.h;
        sx = acx;
        ex = bcx;
        const off = Math.max(36, Math.abs(ey - sy) * 0.45) * (dy >= 0 ? 1 : -1);
        c1x = sx;
        c1y = sy + off;
        c2x = ex;
        c2y = ey - off;
    }

    return {
        d: `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey}`,
        sx,
        sy,
        ex,
        ey,
        midX: (sx + 3 * c1x + 3 * c2x + ex) / 8,
        midY: (sy + 3 * c1y + 3 * c2y + ey) / 8,
    };
}

/* ------------------------------------------------------------------ */
/* Right panel: transition rules                                        */
/* ------------------------------------------------------------------ */

function TransitionPanel({
    transition,
    stages,
    fields,
    roles,
    objects,
    objectId,
    reverseExists,
    onAddReverse,
    onChange,
    onDelete,
}) {
    const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);

    const from = stages.find((s) => s.key === transition.fromKey);
    const to = stages.find((s) => s.key === transition.toKey);

    const patch = (p) => onChange({ ...transition, ...p });

    const updateGuard = (i, p) =>
        patch({
            guards: transition.guards.map((g, gi) =>
                gi === i ? { ...g, ...p } : g,
            ),
        });

    const updateAction = (i, p) =>
        patch({
            actions: transition.actions.map((a, ai) =>
                ai === i ? { ...a, ...p } : a,
            ),
        });

    const availableRoles = roles.filter(
        (r) => !(transition.roleIds ?? []).includes(r.id),
    );

    const inputSx = { fontSize: 13, background: "var(--bg-secondary)" };

    return (
        <Stack sx={{ height: "100%", overflow: "hidden" }}>
            <Stack
                sx={{
                    p: "22px 24px 18px",
                    borderBottom: "1px solid var(--border)",
                    gap: 1.25,
                }}
            >
                <Typography sx={PANEL_LABEL_SX}>TRANSITION RULES</Typography>
                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1.25,
                        fontSize: 15,
                        fontWeight: 600,
                        flexWrap: "wrap",
                    }}
                >
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Stack
                            component="span"
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "2px",
                                background: from?.color ?? "var(--border-strong)",
                            }}
                        />
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>
                            {transition.fromKey == null
                                ? "Any stage"
                                : (from?.name ?? "?")}
                        </Typography>
                    </Stack>
                    <ArrowForwardRounded
                        sx={{ fontSize: 16, color: "var(--accent)" }}
                    />
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Stack
                            component="span"
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "2px",
                                background: to?.color ?? "var(--border-strong)",
                            }}
                        />
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>
                            {to?.name ?? "?"}
                        </Typography>
                    </Stack>
                </Stack>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={transition.fromKey == null}
                            onChange={(e) =>
                                patch({
                                    fromKey: e.target.checked
                                        ? null
                                        : (stages.find(
                                              (s) =>
                                                  s.key !== transition.toKey,
                                          )?.key ?? null),
                                })
                            }
                            sx={{
                                color: "var(--border-strong)",
                                "&.Mui-checked": { color: "var(--accent)" },
                            }}
                        />
                    }
                    label={
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "var(--text-secondary)",
                            }}
                        >
                            From any stage
                        </Typography>
                    }
                />
            </Stack>

            <Stack sx={{ flex: 1, overflowY: "auto", p: "20px 24px", gap: 3.5 }}>
                {/* GUARDS */}
                <Stack sx={{ gap: 1.5 }}>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography sx={SECTION_SX}>GUARDS</Typography>
                        <Typography
                            sx={{ fontSize: 12, color: "var(--text-tertiary)" }}
                        >
                            All must pass
                        </Typography>
                    </Stack>

                    {transition.guards.map((g, i) => (
                        <Stack
                            key={i}
                            sx={{
                                border: "1px solid var(--border-mid)",
                                borderRadius: "4px",
                                p: "10px 12px",
                                gap: 1,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Select
                                    size="small"
                                    fullWidth
                                    value={g.field ?? ""}
                                    onChange={(e) =>
                                        updateGuard(i, {
                                            field: e.target.value,
                                        })
                                    }
                                    sx={inputSx}
                                >
                                    {fields.map((f) => (
                                        <MenuItem
                                            key={f.key}
                                            value={f.key}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {f.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Stack
                                    onClick={() =>
                                        patch({
                                            guards: transition.guards.filter(
                                                (_g, gi) => gi !== i,
                                            ),
                                        })
                                    }
                                    sx={{
                                        cursor: "pointer",
                                        color: "var(--text-tertiary)",
                                        "&:hover": { color: "var(--error)" },
                                    }}
                                >
                                    <CloseRounded sx={{ fontSize: 16 }} />
                                </Stack>
                            </Stack>
                            <Stack sx={{ flexDirection: "row", gap: 1 }}>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={g.op ?? "not_empty"}
                                    onChange={(e) =>
                                        updateGuard(i, { op: e.target.value })
                                    }
                                    sx={inputSx}
                                >
                                    {OPS.map((o) => (
                                        <MenuItem
                                            key={o.key}
                                            value={o.key}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {opNeedsValue(g.op) && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Value"
                                        value={g.value ?? ""}
                                        onChange={(e) =>
                                            updateGuard(i, {
                                                value: e.target.value,
                                            })
                                        }
                                        InputProps={{ sx: inputSx }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    ))}

                    <Stack
                        onClick={() =>
                            patch({
                                guards: [
                                    ...transition.guards,
                                    {
                                        field: fields[0]?.key ?? "",
                                        op: "not_empty",
                                        value: "",
                                    },
                                ],
                            })
                        }
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            color: "var(--accent)",
                            fontSize: 13,
                            fontWeight: 600,
                            width: "fit-content",
                        }}
                    >
                        <AddRounded sx={{ fontSize: 15 }} />
                        Add guard
                    </Stack>
                </Stack>

                {/* WHO CAN MOVE IT */}
                <Stack sx={{ gap: 1.5 }}>
                    <Typography sx={SECTION_SX}>WHO CAN MOVE IT</Typography>
                    <Stack
                        sx={{ flexDirection: "row", flexWrap: "wrap", gap: 1 }}
                    >
                        {(transition.roleIds ?? []).map((roleId) => (
                            <Chip
                                key={roleId}
                                size="small"
                                label={
                                    roles.find((r) => r.id === roleId)?.name ??
                                    "Role"
                                }
                                onDelete={() => {
                                    const next = (
                                        transition.roleIds ?? []
                                    ).filter((id) => id !== roleId);
                                    patch({
                                        roleIds: next.length ? next : null,
                                    });
                                }}
                                sx={{
                                    background: "var(--bg-tint-soft)",
                                    border: "1px solid var(--border-mid)",
                                    fontWeight: 600,
                                    fontSize: 12.5,
                                }}
                            />
                        ))}
                        <Chip
                            size="small"
                            icon={<AddRounded sx={{ fontSize: 14 }} />}
                            label="Role"
                            onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
                            sx={{
                                border: "1px dashed var(--border-strong)",
                                background: "transparent",
                                color: "var(--text-secondary)",
                                fontWeight: 600,
                                fontSize: 12.5,
                                cursor: "pointer",
                            }}
                        />
                    </Stack>
                    <Typography
                        sx={{ fontSize: 12, color: "var(--text-tertiary)" }}
                    >
                        {transition.roleIds == null
                            ? "No roles selected — anyone can move it"
                            : "Only these roles can make this move"}
                    </Typography>
                    <Menu
                        anchorEl={roleMenuAnchor}
                        open={!!roleMenuAnchor}
                        onClose={() => setRoleMenuAnchor(null)}
                    >
                        {availableRoles.length === 0 && (
                            <MenuItem disabled sx={{ fontSize: 13 }}>
                                All roles added
                            </MenuItem>
                        )}
                        {availableRoles.map((r) => (
                            <MenuItem
                                key={r.id}
                                sx={{ fontSize: 13.5 }}
                                onClick={() => {
                                    patch({
                                        roleIds: [
                                            ...(transition.roleIds ?? []),
                                            r.id,
                                        ],
                                    });
                                    setRoleMenuAnchor(null);
                                }}
                            >
                                {r.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>

                {/* WHEN IT MOVES */}
                <Stack sx={{ gap: 1.5 }}>
                    <Typography sx={SECTION_SX}>WHEN IT MOVES</Typography>
                    {transition.actions.map((a, i) => (
                        <Stack
                            key={i}
                            sx={{
                                border: "1px solid var(--border-mid)",
                                borderRadius: "4px",
                                p: "10px 12px",
                                gap: 1,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Select
                                    size="small"
                                    fullWidth
                                    value={a.type ?? "update_field"}
                                    onChange={(e) => {
                                        const type = e.target.value;
                                        const base = { type };
                                        if (type === "update_field") {
                                            base.field = fields[0]?.key ?? "";
                                            base.value = "";
                                        }
                                        if (type === "send_email") {
                                            base.to = "owner";
                                            base.subject = "";
                                            base.body = "";
                                        }
                                        if (type === "webhook") base.url = "";
                                        patch({
                                            actions: transition.actions.map(
                                                (act, ai) =>
                                                    ai === i ? base : act,
                                            ),
                                        });
                                    }}
                                    sx={inputSx}
                                >
                                    {ACTION_TYPES.map((t) => (
                                        <MenuItem
                                            key={t.key}
                                            value={t.key}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {t.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Stack
                                    onClick={() =>
                                        patch({
                                            actions: transition.actions.filter(
                                                (_a, ai) => ai !== i,
                                            ),
                                        })
                                    }
                                    sx={{
                                        cursor: "pointer",
                                        color: "var(--text-tertiary)",
                                        "&:hover": { color: "var(--error)" },
                                    }}
                                >
                                    <CloseRounded sx={{ fontSize: 16 }} />
                                </Stack>
                            </Stack>

                            {a.type === "update_field" && (
                                <Stack sx={{ flexDirection: "row", gap: 1 }}>
                                    <Select
                                        size="small"
                                        fullWidth
                                        value={a.field ?? ""}
                                        onChange={(e) =>
                                            updateAction(i, {
                                                field: e.target.value,
                                            })
                                        }
                                        sx={inputSx}
                                    >
                                        {fields.map((f) => (
                                            <MenuItem
                                                key={f.key}
                                                value={f.key}
                                                sx={{ fontSize: 13 }}
                                            >
                                                {f.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="New value"
                                        value={a.value ?? ""}
                                        onChange={(e) =>
                                            updateAction(i, {
                                                value: e.target.value,
                                            })
                                        }
                                        InputProps={{ sx: inputSx }}
                                    />
                                </Stack>
                            )}

                            {a.type === "send_email" && (
                                <Stack sx={{ gap: 1 }}>
                                    <Stack
                                        sx={{ flexDirection: "row", gap: 1 }}
                                    >
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={a.to ?? "owner"}
                                            onChange={(e) =>
                                                updateAction(i, {
                                                    to: e.target.value,
                                                })
                                            }
                                            sx={inputSx}
                                        >
                                            <MenuItem
                                                value="owner"
                                                sx={{ fontSize: 13 }}
                                            >
                                                Record owner
                                            </MenuItem>
                                            <MenuItem
                                                value="address"
                                                sx={{ fontSize: 13 }}
                                            >
                                                Specific address
                                            </MenuItem>
                                        </Select>
                                        {a.to === "address" && (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="name@company.com"
                                                value={a.address ?? ""}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        address:
                                                            e.target.value,
                                                    })
                                                }
                                                InputProps={{ sx: inputSx }}
                                            />
                                        )}
                                    </Stack>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Subject"
                                        value={a.subject ?? ""}
                                        onChange={(e) =>
                                            updateAction(i, {
                                                subject: e.target.value,
                                            })
                                        }
                                        InputProps={{ sx: inputSx }}
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Body — {{field_key}} merge tags work"
                                        value={a.body ?? ""}
                                        onChange={(e) =>
                                            updateAction(i, {
                                                body: e.target.value,
                                            })
                                        }
                                        InputProps={{ sx: inputSx }}
                                    />
                                </Stack>
                            )}

                            {a.type === "webhook" && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="https://example.com/hook"
                                    value={a.url ?? ""}
                                    onChange={(e) =>
                                        updateAction(i, {
                                            url: e.target.value,
                                        })
                                    }
                                    InputProps={{ sx: inputSx }}
                                />
                            )}

                            {a.type === "create_record" && (
                                <CreateRecordAction
                                    action={a}
                                    objects={objects}
                                    sourceObjectId={objectId}
                                    onChange={(p) => updateAction(i, p)}
                                    inputSx={inputSx}
                                />
                            )}
                        </Stack>
                    ))}

                    <Stack
                        onClick={() =>
                            patch({
                                actions: [
                                    ...transition.actions,
                                    {
                                        type: "update_field",
                                        field: fields[0]?.key ?? "",
                                        value: "",
                                    },
                                ],
                            })
                        }
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            color: "var(--accent)",
                            fontSize: 13,
                            fontWeight: 600,
                            width: "fit-content",
                        }}
                    >
                        <AddRounded sx={{ fontSize: 15 }} />
                        Add action
                    </Stack>
                </Stack>

                {/* A forward-only pipeline dead-ends a record, so make the
                    way back one click rather than a second drag. */}
                {transition.fromKey && !reverseExists && (
                    <Stack sx={{ gap: 0.5 }}>
                        <Stack
                            onClick={onAddReverse}
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 0.75,
                                cursor: "pointer",
                                color: "var(--accent)",
                                fontSize: 13,
                                fontWeight: 600,
                                width: "fit-content",
                            }}
                        >
                            <AddRounded sx={{ fontSize: 15 }} />
                            Allow the move back ({to?.name} → {from?.name})
                        </Stack>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: "var(--text-tertiary)",
                                lineHeight: 1.5,
                            }}
                        >
                            Records can only move along an arrow, so without
                            this one they can&apos;t come back.
                        </Typography>
                    </Stack>
                )}

                <Stack
                    onClick={onDelete}
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 0.75,
                        cursor: "pointer",
                        color: "var(--error)",
                        fontSize: 13,
                        fontWeight: 600,
                        width: "fit-content",
                    }}
                >
                    <CloseRounded sx={{ fontSize: 15 }} />
                    Delete transition
                </Stack>
            </Stack>

            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "16px 24px",
                    borderTop: "1px solid var(--border)",
                }}
            >
                <Stack>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        Transition active
                    </Typography>
                    <Typography
                        sx={{ fontSize: 12, color: "var(--text-tertiary)" }}
                    >
                        Turn off to block this move
                    </Typography>
                </Stack>
                <Switch
                    checked={transition.isActive}
                    onChange={(e) => patch({ isActive: e.target.checked })}
                />
            </Stack>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Right panel: stage properties                                        */
/* ------------------------------------------------------------------ */

function StagePanel({ stage, onChange, onDelete }) {
    const patch = (p) => onChange({ ...stage, ...p });

    return (
        <Stack sx={{ height: "100%", overflow: "hidden" }}>
            <Stack
                sx={{
                    p: "22px 24px 18px",
                    borderBottom: "1px solid var(--border)",
                    gap: 1,
                }}
            >
                <Typography sx={PANEL_LABEL_SX}>STAGE</Typography>
                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Stack
                        component="span"
                        sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "2px",
                            background: stage.color,
                        }}
                    />
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                        {stage.name || "Untitled stage"}
                    </Typography>
                </Stack>
            </Stack>

            <Stack sx={{ flex: 1, overflowY: "auto", p: "20px 24px", gap: 3 }}>
                <TextField
                    label="Stage name"
                    value={stage.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    fullWidth
                />

                <Stack sx={{ gap: 1.25 }}>
                    <Typography sx={SECTION_SX}>COLOR</Typography>
                    <Stack sx={{ flexDirection: "row", gap: 1 }}>
                        {STAGE_COLORS.map((c) => (
                            <Stack
                                key={c}
                                onClick={() => patch({ color: c })}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "4px",
                                    background: c,
                                    cursor: "pointer",
                                    border:
                                        stage.color === c
                                            ? "2px solid var(--text)"
                                            : "2px solid transparent",
                                    outline:
                                        stage.color === c
                                            ? "1px solid var(--bg)"
                                            : "none",
                                    outlineOffset: -3,
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>

                <Stack sx={{ gap: 1.25 }}>
                    <Typography sx={SECTION_SX}>KIND</Typography>
                    <Stack sx={{ flexDirection: "row", gap: 1 }}>
                        {[
                            { key: "open", label: "Open" },
                            { key: "won", label: "Won" },
                            { key: "lost", label: "Lost" },
                        ].map((k) => (
                            <Chip
                                key={k.key}
                                label={k.label}
                                size="small"
                                onClick={() => patch({ kind: k.key })}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 12.5,
                                    cursor: "pointer",
                                    background:
                                        stage.kind === k.key
                                            ? "var(--primary)"
                                            : "transparent",
                                    color:
                                        stage.kind === k.key
                                            ? "var(--primary-contrast)"
                                            : "var(--text-secondary)",
                                    border:
                                        stage.kind === k.key
                                            ? "1px solid var(--primary)"
                                            : "1px solid var(--border-mid)",
                                    "&:hover": {
                                        background:
                                            stage.kind === k.key
                                                ? "var(--primary)"
                                                : "var(--bg-tint-soft)",
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>

                <TextField
                    label="Probability (%)"
                    type="number"
                    value={stage.probability ?? ""}
                    onChange={(e) => patch({ probability: e.target.value })}
                    inputProps={{ min: 0, max: 100 }}
                    fullWidth
                />

                <Stack
                    onClick={onDelete}
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 0.75,
                        cursor: "pointer",
                        color: "var(--error)",
                        fontSize: 13,
                        fontWeight: 600,
                        width: "fit-content",
                    }}
                >
                    <CloseRounded sx={{ fontSize: 15 }} />
                    Delete stage
                </Stack>
            </Stack>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* The builder                                                          */
/* ------------------------------------------------------------------ */

export default function PipelineBuilder() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { objects, loading: objectsLoading } = useObjects();
    const object = objects.find((o) => o.slug === slug) ?? null;

    const [pipelineId, setPipelineId] = useState(null);
    const [pipelineName, setPipelineName] = useState("");
    const [stages, setStages] = useState([]);
    const [transitions, setTransitions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [dirty, setDirty] = useState(false);
    /* Editing pipelines is its own permission, separate from the data model. */
    const canManage = can("pipelines", "manage");
    const [loading, setLoading] = useState(true);
    const [noPipeline, setNoPipeline] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [roles, setRoles] = useState([]);
    const [linking, setLinking] = useState(null);
    const [deletingStage, setDeletingStage] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const canvasRef = useRef(null);
    const dragRef = useRef(null);

    const fields = useMemo(
        () =>
            [...(object?.fields ?? [])].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
            ),
        [object],
    );

    /* Load org roles for the WHO CAN MOVE IT chips. */
    useEffect(() => {
        (async () => {
            try {
                const { data } = await listOrgRoles();
                setRoles(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (error) {
                console.error("Failed to load roles", { error });
            }
        })();
    }, []);

    const applyGraph = useCallback((p) => {
        const { stages: s, transitions: t } = toLocalGraph(p);
        setPipelineId(p.id);
        setPipelineName(p.name ?? "Pipeline");
        setStages(s);
        setTransitions(t);
        setDirty(false);
        setSelected(null);
    }, []);

    useEffect(() => {
        if (!object) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await listPipelines(object.id);
                const list = Array.isArray(data) ? data : (data?.data ?? []);
                if (list.length === 0) {
                    setNoPipeline(true);
                } else {
                    setNoPipeline(false);
                    applyGraph(
                        list.find((p) => p.isDefault) ?? list[0],
                    );
                }
            } catch (err) {
                toast.error(apiError(err, "Failed to load the pipeline"));
            } finally {
                setLoading(false);
            }
        })();
    }, [object, applyGraph]);

    /* Unsaved-changes guard. */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /* Live preview arrow while dragging from a connection handle. */
    useEffect(() => {
        if (!linking) return;
        const move = (e) => {
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            setLinking((prev) =>
                prev
                    ? {
                          ...prev,
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                      }
                    : prev,
            );
        };
        const up = (e) => {
            const rect = canvasRef.current?.getBoundingClientRect();
            setLinking((prev) => {
                if (prev && rect) {
                    const px = e.clientX - rect.left;
                    const py = e.clientY - rect.top;
                    const target = stages.find(
                        (s) =>
                            s.key !== prev.fromKey &&
                            px >= s.x &&
                            px <= s.x + NODE_W &&
                            py >= s.y &&
                            py <= s.y + NODE_H,
                    );
                    if (target) {
                        const existing = transitions.find(
                            (t) =>
                                t.fromKey === prev.fromKey &&
                                t.toKey === target.key,
                        );
                        if (existing) {
                            setSelected({
                                type: "transition",
                                key: existing.key,
                            });
                        } else {
                            const key = tmpKey("t");
                            setTransitions((ts) => [
                                ...ts,
                                {
                                    key,
                                    id: null,
                                    fromKey: prev.fromKey,
                                    toKey: target.key,
                                    guards: [],
                                    roleIds: null,
                                    actions: [],
                                    isActive: true,
                                },
                            ]);
                            setSelected({ type: "transition", key });
                            setDirty(true);
                        }
                    }
                }
                return null;
            });
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        return () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
        };
    }, [linking, stages, transitions]);

    function startNodeDrag(e, stage) {
        if (e.button !== 0) return;
        const rect = canvasRef.current.getBoundingClientRect();
        dragRef.current = {
            key: stage.key,
            dx: e.clientX - rect.left - stage.x,
            dy: e.clientY - rect.top - stage.y,
            moved: false,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    }

    function onNodeDrag(e) {
        const drag = dragRef.current;
        if (!drag) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = Math.max(0, e.clientX - rect.left - drag.dx);
        const y = Math.max(0, e.clientY - rect.top - drag.dy);
        drag.moved = true;
        setStages((prev) =>
            prev.map((s) => (s.key === drag.key ? { ...s, x, y } : s)),
        );
    }

    function endNodeDrag(e, stage) {
        const drag = dragRef.current;
        dragRef.current = null;
        if (drag && !drag.moved) {
            setSelected({ type: "stage", key: stage.key });
        } else if (drag?.moved) {
            setDirty(true);
        }
    }

    function addStage() {
        const key = tmpKey("s");
        const i = stages.length;
        setStages((prev) => [
            ...prev,
            {
                key,
                id: null,
                name: `Stage ${i + 1}`,
                color: STAGE_COLORS[i % STAGE_COLORS.length],
                kind: "open",
                probability: "",
                x: 56 + (i % 3) * 274,
                y: 146 + Math.floor(i / 3) * 212,
            },
        ]);
        setSelected({ type: "stage", key });
        setDirty(true);
    }

    function updateStage(next) {
        setStages((prev) =>
            prev.map((s) => (s.key === next.key ? next : s)),
        );
        setDirty(true);
    }

    function removeStage(stage) {
        setStages((prev) => prev.filter((s) => s.key !== stage.key));
        setTransitions((prev) =>
            prev.filter(
                (t) => t.fromKey !== stage.key && t.toKey !== stage.key,
            ),
        );
        setSelected(null);
        setDeletingStage(null);
        setDirty(true);
    }

    function updateTransition(next) {
        setTransitions((prev) =>
            prev.map((t) => (t.key === next.key ? next : t)),
        );
        setDirty(true);
    }

    function removeTransition(key) {
        setTransitions((prev) => prev.filter((t) => t.key !== key));
        setSelected(null);
        setDirty(true);
    }

    /** Mirror a transition so records can move back the way they came. */
    function addReverseTransition(transition) {
        const reverse = {
            key: tmpKey("t"),
            id: null,
            fromKey: transition.toKey,
            toKey: transition.fromKey,
            guards: [],
            roleIds: null,
            actions: [],
            isActive: true,
        };
        setTransitions((prev) => [...prev, reverse]);
        setSelected({ type: "transition", key: reverse.key });
        setDirty(true);
    }

    async function handleSave() {
        if (stages.some((s) => !s.name.trim())) {
            toast.error("Every stage needs a name");
            return;
        }
        setSaving(true);
        try {
            const { data } = await savePipeline(
                pipelineId,
                toPayload(pipelineName.trim() || "Pipeline", stages, transitions),
            );
            applyGraph(data);
            toast.success("Pipeline saved");
        } catch (err) {
            toast.error(apiError(err, "Could not save the pipeline"));
        } finally {
            setSaving(false);
        }
    }

    async function handleCreatePipeline() {
        setCreating(true);
        try {
            const { data } = await createPipeline(object.id, {
                name: `${object.name} pipeline`,
            });
            applyGraph(data);
            setNoPipeline(false);
        } catch (err) {
            toast.error(apiError(err, "Could not create the pipeline"));
        } finally {
            setCreating(false);
        }
    }

    /* -------------------------------------------------------------- */

    if (objectsLoading || (object && loading)) {
        return (
            <Stack sx={{ alignItems: "center", py: 12 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (!object) {
        return (
            <Stack sx={{ alignItems: "center", py: 12, gap: 2 }}>
                <Typography variant="h6">Object not found</Typography>
                <Button
                    variant="soft-outlined"
                    onClick={() => navigate("/admin/objects")}
                >
                    Open the object builder
                </Button>
            </Stack>
        );
    }

    if (noPipeline) {
        return (
            <Stack sx={{ alignItems: "center", py: 10, gap: 2 }}>
                <Typography
                    sx={{
                        fontFamily: "'Newsreader', Georgia, serif",
                        fontSize: 26,
                        fontWeight: 500,
                    }}
                >
                    {object.name} has no pipeline yet
                </Typography>
                <Typography
                    sx={{
                        fontSize: 14,
                        color: "var(--text-secondary)",
                        maxWidth: 440,
                        textAlign: "center",
                        lineHeight: 1.6,
                    }}
                >
                    A pipeline defines the stages records move through and the
                    rules that guard each move.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    loading={creating}
                    startIcon={<AddRounded />}
                    onClick={handleCreatePipeline}
                >
                    Create pipeline
                </Button>
            </Stack>
        );
    }

    const selectedStage =
        selected?.type === "stage"
            ? stages.find((s) => s.key === selected.key)
            : null;
    const selectedTransition =
        selected?.type === "transition"
            ? transitions.find((t) => t.key === selected.key)
            : null;

    const hasAnyStageTransitions = transitions.some((t) => t.fromKey == null);

    const stageBox = (key) => {
        if (key == null) return { ...ANY_NODE };
        const s = stages.find((st) => st.key === key);
        return s ? { x: s.x, y: s.y, w: NODE_W, h: NODE_H } : null;
    };

    return (
        <Stack
            sx={{
                height: "calc(100vh - 60px - 48px)",
                minHeight: 540,
                border: "1px solid var(--border)",
                borderRadius: "6px",
                overflow: "hidden",
                background: "var(--bg-secondary)",
            }}
        >
            {/* ---------- top bar ---------- */}
            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: 58,
                    px: 2.5,
                    borderBottom: "1px solid var(--border)",
                    flexShrink: 0,
                }}
            >
                <Stack
                    sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                    <Typography
                        component={Link}
                        to={`/o/${slug}`}
                        sx={{
                            fontSize: 13.5,
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            "&:hover": { color: "var(--text)" },
                        }}
                    >
                        <ObjectIcon name={object.icon} size={15} />
                        {object.name}
                    </Typography>
                    <ChevronRightRounded
                        sx={{ fontSize: 16, color: "var(--border-strong)" }}
                    />
                    <InputBase
                        value={pipelineName}
                        onChange={(e) => {
                            setPipelineName(e.target.value);
                            setDirty(true);
                        }}
                        sx={{
                            fontSize: 14.5,
                            fontWeight: 600,
                            color: "var(--text)",
                            width: 220,
                            "& input": { p: 0 },
                        }}
                    />
                    {dirty && (
                        <Chip
                            size="small"
                            label="DRAFT"
                            sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: "0.8px",
                                background: "var(--bg-tint-soft)",
                                border: "1px solid var(--border-mid)",
                                color: "var(--text-secondary)",
                            }}
                        />
                    )}
                </Stack>
                <Stack
                    sx={{ flexDirection: "row", alignItems: "center", gap: 1.5 }}
                >
                    {pipelineId && (
                        <Button
                            variant="text"
                            sx={{ height: 38 }}
                            onClick={() => setHistoryOpen(true)}
                        >
                            History
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        loading={saving}
                        disabled={!dirty || !canManage}
                        onClick={handleSave}
                        sx={{ height: 38 }}
                    >
                        Save pipeline
                    </Button>
                    {!canManage && (
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: "var(--text-tertiary)",
                                maxWidth: 180,
                                lineHeight: 1.4,
                            }}
                        >
                            Your role can view this pipeline but not change it.
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {pipelineId && (
                <VersionHistoryDrawer
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    kind="pipeline"
                    entityId={pipelineId}
                    title={pipelineName || "Pipeline"}
                />
            )}

            <Stack sx={{ flexDirection: "row", flex: 1, minHeight: 0 }}>
                {/* ---------- left panel ---------- */}
                <Stack
                    sx={{
                        width: 240,
                        flexShrink: 0,
                        borderRight: "1px solid var(--border)",
                        py: 2.5,
                        overflowY: "auto",
                    }}
                >
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2.5,
                            pb: 1.5,
                        }}
                    >
                        <Typography sx={PANEL_LABEL_SX}>STAGES</Typography>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: "var(--text-tertiary)",
                            }}
                        >
                            {stages.length}
                        </Typography>
                    </Stack>

                    {stages.map((s) => {
                        const active =
                            selected?.type === "stage" &&
                            selected.key === s.key;
                        return (
                            <Stack
                                key={s.key}
                                onClick={() =>
                                    setSelected({ type: "stage", key: s.key })
                                }
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 2.5,
                                    py: 1.25,
                                    fontSize: 14,
                                    fontWeight: active ? 600 : 500,
                                    cursor: "pointer",
                                    background: active
                                        ? "var(--bg-tint-soft)"
                                        : "transparent",
                                    borderLeft: active
                                        ? "2px solid var(--text)"
                                        : "2px solid transparent",
                                    "&:hover": {
                                        background: "var(--bg-tint-soft)",
                                    },
                                }}
                            >
                                <Stack
                                    component="span"
                                    sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: "2px",
                                        background: s.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: "inherit",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {s.name || "Untitled"}
                                </Typography>
                            </Stack>
                        );
                    })}

                    <Stack
                        onClick={addStage}
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            mx: 2.5,
                            mt: 1.75,
                            height: 40,
                            border: "1px dashed var(--border-strong)",
                            borderRadius: "3px",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-strong-muted)",
                            cursor: "pointer",
                            "&:hover": { background: "var(--bg-tint-soft)" },
                        }}
                    >
                        <AddRounded sx={{ fontSize: 15 }} />
                        Add stage
                    </Stack>

                    <Stack sx={{ mt: 4, px: 2.5, gap: 1.5 }}>
                        <Typography sx={PANEL_LABEL_SX}>
                            HOW IT WORKS
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 13,
                                lineHeight: 1.6,
                                color: "var(--text-secondary)",
                            }}
                        >
                            Drag from a stage's edge to another stage to allow
                            that move. Click any arrow to set its rules.
                        </Typography>
                    </Stack>
                </Stack>

                {/* ---------- canvas ---------- */}
                <Stack
                    ref={canvasRef}
                    onClick={() => setSelected(null)}
                    sx={{
                        flex: 1,
                        position: "relative",
                        minWidth: 0,
                        overflow: "hidden",
                        backgroundColor: "var(--bg)",
                        backgroundImage:
                            "radial-gradient(var(--border-mid) 1px, transparent 1px)",
                        backgroundSize: "22px 22px",
                    }}
                >
                    {/* arrows */}
                    <svg
                        style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                        }}
                    >
                        <defs>
                            <marker
                                id="pb-arrow"
                                markerWidth="9"
                                markerHeight="9"
                                refX="7.5"
                                refY="4.5"
                                orient="auto"
                            >
                                <path
                                    d="M0.5 1 7.5 4.5 0.5 8"
                                    fill="none"
                                    stroke="#8B8781"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </marker>
                            <marker
                                id="pb-arrow-dashed"
                                markerWidth="9"
                                markerHeight="9"
                                refX="7.5"
                                refY="4.5"
                                orient="auto"
                            >
                                <path
                                    d="M0.5 1 7.5 4.5 0.5 8"
                                    fill="none"
                                    stroke="#B8B5AF"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </marker>
                            <marker
                                id="pb-arrow-selected"
                                markerWidth="9"
                                markerHeight="9"
                                refX="7.5"
                                refY="4.5"
                                orient="auto"
                            >
                                <path
                                    d="M0.5 1 7.5 4.5 0.5 8"
                                    fill="none"
                                    stroke="#1E6B4F"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </marker>
                        </defs>

                        {transitions.map((t) => {
                            const from = stageBox(t.fromKey);
                            const to = stageBox(t.toKey);
                            if (!from || !to) return null;
                            const geo = edgeGeometry(from, to);
                            const isSelected =
                                selected?.type === "transition" &&
                                selected.key === t.key;
                            const isAny = t.fromKey == null;
                            const stroke = isSelected
                                ? "#1E6B4F"
                                : isAny || !t.isActive
                                  ? "#B8B5AF"
                                  : "#8B8781";
                            return (
                                <g key={t.key}>
                                    <path
                                        d={geo.d}
                                        stroke="transparent"
                                        strokeWidth="14"
                                        fill="none"
                                        style={{
                                            pointerEvents: "stroke",
                                            cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelected({
                                                type: "transition",
                                                key: t.key,
                                            });
                                        }}
                                    />
                                    <path
                                        d={geo.d}
                                        stroke={stroke}
                                        strokeWidth={isSelected ? 2 : 1.4}
                                        strokeDasharray={
                                            isAny || !t.isActive
                                                ? "5 5"
                                                : undefined
                                        }
                                        fill="none"
                                        markerEnd={`url(#${
                                            isSelected
                                                ? "pb-arrow-selected"
                                                : isAny || !t.isActive
                                                  ? "pb-arrow-dashed"
                                                  : "pb-arrow"
                                        })`}
                                    />
                                    {isSelected && (
                                        <>
                                            <circle
                                                cx={geo.sx}
                                                cy={geo.sy}
                                                r="5"
                                                fill="#FFFFFF"
                                                stroke="#1E6B4F"
                                                strokeWidth="1.6"
                                            />
                                            <circle
                                                cx={geo.ex}
                                                cy={geo.ey}
                                                r="5"
                                                fill="#FFFFFF"
                                                stroke="#1E6B4F"
                                                strokeWidth="1.6"
                                            />
                                        </>
                                    )}
                                </g>
                            );
                        })}

                        {/* live preview while linking */}
                        {linking && (
                            <path
                                d={(() => {
                                    const from = stageBox(linking.fromKey);
                                    if (!from) return "";
                                    return edgeGeometry(from, {
                                        x: linking.x - 1,
                                        y: linking.y - 1,
                                        w: 2,
                                        h: 2,
                                    }).d;
                                })()}
                                stroke="#1E6B4F"
                                strokeWidth="1.6"
                                strokeDasharray="5 5"
                                fill="none"
                                markerEnd="url(#pb-arrow-selected)"
                            />
                        )}
                    </svg>

                    {/* selected-arrow pill label */}
                    {selectedTransition &&
                        (() => {
                            const from = stageBox(selectedTransition.fromKey);
                            const to = stageBox(selectedTransition.toKey);
                            if (!from || !to) return null;
                            const geo = edgeGeometry(from, to);
                            return (
                                <Stack
                                    sx={{
                                        position: "absolute",
                                        left: geo.midX,
                                        top: geo.midY,
                                        transform: "translate(-50%, -50%)",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 0.75,
                                        background: "var(--accent)",
                                        color: "#FFFFFF",
                                        borderRadius: "20px",
                                        px: 1.75,
                                        py: 0.6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        boxShadow:
                                            "0 2px 8px rgba(30,107,79,0.25)",
                                        pointerEvents: "none",
                                        whiteSpace: "nowrap",
                                        zIndex: 2,
                                    }}
                                >
                                    {selectedTransition.guards.length} guard
                                    {selectedTransition.guards.length === 1
                                        ? ""
                                        : "s"}{" "}
                                    · {selectedTransition.actions.length}{" "}
                                    action
                                    {selectedTransition.actions.length === 1
                                        ? ""
                                        : "s"}
                                </Stack>
                            );
                        })()}

                    {/* ANY chip node */}
                    {hasAnyStageTransitions && (
                        <Stack
                            sx={{
                                position: "absolute",
                                left: ANY_NODE.x,
                                top: ANY_NODE.y,
                                width: ANY_NODE.w,
                                height: ANY_NODE.h,
                                alignItems: "center",
                                justifyContent: "center",
                                background: "var(--bg-tint-soft)",
                                border: "1px dashed var(--border-strong)",
                                borderRadius: "20px",
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "1.5px",
                                color: "var(--text-secondary)",
                            }}
                        >
                            ANY
                        </Stack>
                    )}

                    {/* stage nodes */}
                    {stages.map((s) => {
                        const isSelected =
                            selected?.type === "stage" &&
                            selected.key === s.key;
                        const isWon = s.kind === "won";
                        const isLost = s.kind === "lost";
                        const subtitle = isWon
                            ? "Closing stage"
                            : isLost
                              ? "Closing stage — lost"
                              : s.probability !== "" && s.probability != null
                                ? `${s.probability}% likely`
                                : "Open stage";
                        return (
                            <Stack
                                key={s.key}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                    startNodeDrag(e, s);
                                }}
                                onPointerMove={onNodeDrag}
                                onPointerUp={(e) => endNodeDrag(e, s)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    position: "absolute",
                                    left: s.x,
                                    top: s.y,
                                    width: NODE_W,
                                    height: NODE_H,
                                    background: isWon
                                        ? "var(--accent-soft)"
                                        : isLost
                                          ? "var(--bg-tint-soft)"
                                          : "var(--bg-secondary)",
                                    border: isSelected
                                        ? "1.5px solid var(--accent)"
                                        : isWon
                                          ? "1px solid var(--accent)"
                                          : isLost
                                            ? "1px dashed var(--border-strong)"
                                            : "1px solid var(--border-mid)",
                                    borderRadius: "6px",
                                    boxShadow: isSelected
                                        ? "0 2px 10px rgba(30,107,79,0.12)"
                                        : "0 1px 4px rgba(23,23,21,0.05)",
                                    justifyContent: "center",
                                    gap: 0.4,
                                    px: 2,
                                    cursor: "grab",
                                    touchAction: "none",
                                    userSelect: "none",
                                    "&:active": { cursor: "grabbing" },
                                }}
                            >
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Stack
                                        component="span"
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "2px",
                                            background: s.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: isWon
                                                ? "var(--accent)"
                                                : isLost
                                                  ? "var(--text-secondary)"
                                                  : "var(--text)",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {s.name || "Untitled"}
                                    </Typography>
                                </Stack>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "var(--text-tertiary)",
                                        pl: 2,
                                    }}
                                >
                                    {subtitle}
                                </Typography>

                                {/* connection handle */}
                                <Stack
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const rect =
                                            canvasRef.current.getBoundingClientRect();
                                        setLinking({
                                            fromKey: s.key,
                                            x: e.clientX - rect.left,
                                            y: e.clientY - rect.top,
                                        });
                                    }}
                                    sx={{
                                        position: "absolute",
                                        right: -7,
                                        top: "50%",
                                        mt: "-7px",
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        background: "var(--bg-secondary)",
                                        border: "1.5px solid var(--border-strong)",
                                        cursor: "crosshair",
                                        "&:hover": {
                                            borderColor: "var(--accent)",
                                            background: "var(--accent-soft)",
                                        },
                                    }}
                                />
                            </Stack>
                        );
                    })}

                    {/* zoom control (visual) */}
                    <Stack
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            position: "absolute",
                            left: 20,
                            bottom: 20,
                            flexDirection: "row",
                            alignItems: "center",
                            background: "var(--bg-secondary)",
                            border: "1px solid var(--border)",
                            borderRadius: "5px",
                            boxShadow: "0 2px 10px rgba(23,23,21,0.06)",
                        }}
                    >
                        <Stack
                            sx={{
                                width: 34,
                                height: 34,
                                alignItems: "center",
                                justifyContent: "center",
                                borderRight: "1px solid var(--border)",
                                color: "var(--text-strong-muted)",
                                fontSize: 15,
                            }}
                        >
                            –
                        </Stack>
                        <Typography
                            sx={{
                                px: 1.5,
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: "var(--text-strong-muted)",
                            }}
                        >
                            100%
                        </Typography>
                        <Stack
                            sx={{
                                width: 34,
                                height: 34,
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: "1px solid var(--border)",
                                color: "var(--text-strong-muted)",
                                fontSize: 15,
                            }}
                        >
                            +
                        </Stack>
                    </Stack>
                </Stack>

                {/* ---------- right properties panel ---------- */}
                <Stack
                    sx={{
                        width: 336,
                        flexShrink: 0,
                        borderLeft: "1px solid var(--border)",
                        overflow: "hidden",
                    }}
                >
                    {selectedTransition ? (
                        <TransitionPanel
                            transition={selectedTransition}
                            stages={stages}
                            fields={fields}
                            roles={roles}
                            objects={objects}
                            objectId={object?.id}
                            reverseExists={transitions.some(
                                (t) =>
                                    t.fromKey === selectedTransition.toKey &&
                                    t.toKey === selectedTransition.fromKey,
                            )}
                            onAddReverse={() =>
                                addReverseTransition(selectedTransition)
                            }
                            onChange={updateTransition}
                            onDelete={() =>
                                removeTransition(selectedTransition.key)
                            }
                        />
                    ) : selectedStage ? (
                        <StagePanel
                            stage={selectedStage}
                            onChange={updateStage}
                            onDelete={() => setDeletingStage(selectedStage)}
                        />
                    ) : (
                        <Stack
                            sx={{
                                p: "22px 24px",
                                gap: 1.5,
                            }}
                        >
                            <Typography sx={PANEL_LABEL_SX}>
                                PROPERTIES
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13.5,
                                    lineHeight: 1.6,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                Select a stage to edit its name, color and
                                kind — or click an arrow to set the rules for
                                that move: guards, who can make it, and what
                                happens when it does.
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            <Dialog
                open={!!deletingStage}
                onClose={() => setDeletingStage(null)}
            >
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this stage?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{deletingStage?.name}&#8221; and every
                        transition touching it will be removed when you save.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeletingStage(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => removeStage(deletingStage)}
                    >
                        Delete stage
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
